import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import ENDPOINT from '../../../constants/endpoints';
import Button from '../../../GbsUI/UI/Button';
import Checkbox from '../../../GbsUI/UI/Checkbox';
import FileUpload from '../../../GbsUI/UI/FileUpload';
import ArrowLeftIcon from '../../../GbsUI/UI/icons/ArrowLeft';
import ArrowRightIcon from '../../../GbsUI/UI/icons/ArrowRight';
import RawButton from '../../../GbsUI/UI/RawButton';
import TextInput from '../../../GbsUI/UI/TextInput';
import ResponseErrorDialog from '../../../scenes/Dialog/components/ResponseErrorDialog';
import SignupLayout from '../components/Layout';
import {SignupContext} from '../context';
import stepEightSchema from '../schema/step-eight';
import stepFiveSchema from '../schema/step-five';
import { stepFourSchema } from '../schema/step-four';
import stepSevenSchema from '../schema/step-seven';
import stepSixSchema from '../schema/step-six';
import stepThreeSchema from '../schema/step-three';
import stepTwoSchema from '../schema/step-two';

const SignupStepEight = ({history}) => {
  const {state, setState, resetState} = useContext(SignupContext);
  const [formErrors, setFormErrors] = useState(new Map());
  const initialized = useRef(false);
  const [linkedInHelpDetailed, setLinkedInHelpDetailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = useCallback((data, abortEarly = false) => {
    return stepEightSchema.validate(data, {abortEarly}).then((data) => {
      setFormErrors(new Map());
      return data;
    }).catch((errors) => {
      if (errors.inner.length > 0) {
        const errorsMap = errors.inner.reduce((accum, item) => {
          accum.set(item.path, item.errors);
          return accum;
        }, new Map());

        setFormErrors(errorsMap);
      } else {
        const errorsMap = new Map();
        errorsMap.set(errors.path, errors.errors);
        setFormErrors(errorsMap);
      }

      return false;
    });
  }, []);

  const validateFull = useCallback(async () => {
    try {
      await stepTwoSchema.validate(state.education, {abortEarly: true});
    } catch {
      history.push('/signup/step/2');
      return false;
    }

    try {
      await stepThreeSchema.validate(state.testScore, {abortEarly: true});
    } catch {
      history.push('/signup/step/3');
      return false;
    }

    try {
      await stepFourSchema.validate(state.experience, {abortEarly: true});
    } catch {
      history.push('/signup/step/4');
      return false;
    }

    try {
      await stepFiveSchema.validate(state.location, {abortEarly: true});
    } catch {
      history.push('/signup/step/5');
      return false;
    }

    try {
      await stepSixSchema.validate(state.workStatus, {abortEarly: true});
    } catch {
      history.push('/signup/step/6');
      return false;
    }

    try {
      await stepSevenSchema.validate(state.leadership, {abortEarly: true});
    } catch {
      history.push('/signup/step/7');
      return false;
    }

    return validate(state.resume);
  }, [history, state, validate]);

  const onSubmit = useCallback(e => {
    e.preventDefault();

    validateFull().then((valid) => {
      if (valid) {
        setSubmitting(true);
        const formData = new FormData();

        formData.append('profile', JSON.stringify(state));

        if (state.resume.file) {
          formData.append('resume', state.resume.file);
        }

        fetch(ENDPOINT.ACTION.ACCOUNT.FINISH_SIGNUP, {
          method: 'POST',
          body: formData,
        }).then((response) => response.json())
          .then((result) => {
            if (result.status === 'ERROR') {
              setSubmitting(false);
              ResponseErrorDialog({ response: result });
            } else {
              resetState(true);
              history.replace('/signup/success');
            }
          });
      }
    });
  }, [history, resetState, state, validateFull]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    validate(state.resume, true);
  }, [state, validate]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      resume: {
        ...prevState.resume,
        file: null,
      }
    }));
  }, [setState]);

  return (
    <form onSubmit={onSubmit}>
      <SignupLayout
        title={(
          <span className={'flex justify-between gap-2'}>
            <span>
              Step 8: Add LinkedIn and Upload Your Resume
            </span>
            <span className={'text-small'}>
              Step 8 of 8
            </span>
          </span>
        )}
        description={'Provide your LinkedIn profile and resume to match with the most relevant job opportunities.'}
        footer={
          <>
            <Button
              type={'button'}
              variant={'secondary'}
              size={'xl'}
              icon={ArrowLeftIcon}
              iconProps={{size: 21}}
              iconPosition={'left'}
              onClick={() => history.push('/signup/step/7')}
              disabled={submitting}
            >
              Back
            </Button>
            <Button
              size={'xl'}
              icon={ArrowRightIcon}
              iconProps={{size: 21}}
              iconPosition={'right'}
              disabled={submitting}
            >
              Complete registration
            </Button>
          </>
        }
      >
        <div className={'signup-form-row'}>
          <div className={'signup-form-item signup-form-item--full mb-4'}>
            <TextInput
              name={'linkedin'}
              label={'LinkedIn Profile URL'}
              error={formErrors.get('linkedin')}
              help={(
                <>
                  The correct format is
                  {' '}
                  <span className={'nowrap'}>https://www.linkedin.com/in/johnsmith/</span>
                </>
              )}
              value={state?.resume?.linkedin || ''}
              placeholder={'https://www.linkedin.com/in/johnsmith/'}
              pattern={'https://(www.)?linkedin.com/in/.+'}
              onChange={e => {
                setState((prevState) => ({
                  ...prevState,
                  resume: {
                    ...prevState.resume,
                    linkedin: e.target.value,
                  }
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();

                if (val) {
                  let linkedin = '';

                  try {
                    const url = new URL(val);

                    if (url.protocol !== 'https:') {
                      url.protocol = 'https:';
                    }

                    if (url.hostname !== 'www.linkedin.com') {
                      url.hostname = 'www.linkedin.com';
                    }

                    linkedin = url.toString();
                  } catch (e) {
                    const regex = /(www\.)?(linkedin.com)?\/?in\/([\w-]*)\/?/gm;
                    const match = regex.exec(val);

                    if (match) {
                      linkedin = `https://www.linkedin.com/in/${match[3]}`;
                    } else {
                      linkedin = `https://www.linkedin.com/in/${val.replace('/', '')}`;
                    }
                  }

                  setState((prevState) => ({
                    ...prevState,
                    resume: {
                      ...prevState.resume,
                      linkedin,
                    }
                  }));
                }
              }}
            />
            <div className={'text-input__help'}>
              To find your LinkedIn profile link, log into LinkedIn, click on your profile, and copy the URL from the address bar of your browser.
              {' '}
              <RawButton onClick={() => setLinkedInHelpDetailed(!linkedInHelpDetailed)}>Need help finding your profile link?</RawButton>
              {linkedInHelpDetailed && (
                <div className={'linkedin-help-steps'}>
                  <p>1. Log in to LinkedIn: Start by signing into your LinkedIn account.</p>
                  <p>2. Go to Your Profile: Click on your profile picture or name at the top of the LinkedIn homepage to go to your profile
                    page.</p>
                  <p>3. Locate Your LinkedIn URL: Once on your profile page, look for the URL in your browser's address bar. It typically looks like
                    https://www.linkedin.com/in/johnsmith.</p>
                  <p>4. Copy Your Profile URL: Highlight the URL in the address bar, right-click it, and select "Copy" to copy your LinkedIn profile
                    link.</p>
                </div>
              )}
            </div>
          </div>
          {!state?.resume?.uploadLater && (
            <div className={'signup-form-item signup-form-item--full'}>
              <FileUpload
                title={'Drag or click here to upload your resume'}
                description={'You can also upload your resume later in your profile settings'}
                accept={'application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
                maxFileSize={5}
                labelProps={{
                  label: 'Resume Drop',
                  errors: formErrors.get('file'),
                  help: (
                    <div className={'text-right'}>
                      Accepted file types: PDF, DOC, or DOCX<br/>
                      Maximum file size: 5 MB
                    </div>
                  ),
                  showHelpTooltip: false,
                }}
                onChange={(files) => {
                  setState((prevState) => ({
                    ...prevState,
                    resume: {
                      ...prevState.resume,
                      file: files.length > 0 ? files[0] : null,
                    }
                  }));
                }}
              />
            </div>
          )}
          <div className={'signup-form-item signup-form-item--full'}>
            <Checkbox
              checked={!!state?.resume?.uploadLater}
              label={'Upload my resume later'}
              help={!!state?.resume?.uploadLater ? 'You can upload your resume later, but it will be required to connect with recruiters.' : ''}
              onChange={(e) => {
                const uploadLater = e.target.checked;

                setState((prevState) => ({
                  ...prevState,
                  resume: {
                    ...prevState.resume,
                    uploadLater,
                  }
                }));
              }}
            />
          </div>
        </div>
      </SignupLayout>
    </form>
  );
};

export default SignupStepEight;
